import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Satellite, Map as MapIcon } from "lucide-react";
import { usePrefs } from "../../context/PrefsContext";
import { reverseGeocode } from "../../api/geocode";
import { localizeAddress } from "../../lib/addressLang";
import { translateText } from "../../api/ai";
import { MapSearch } from "./MapSearch";
import { DEFAULT_CENTER } from "./mapConfig";
import { useMapLibre } from "./useMapLibre";

// منتقي موقع على الخريطة: بحث + سحب المؤشر + دائرة النطاق.
export const LocationPicker = ({ latitude, longitude, radiusM = 50, onChange, height = "h-[26rem]" }) => {
  const { t, i18n } = useTranslation();
  const { theme } = usePrefs();
  const container = useRef(null);
  const [satellite, setSatellite] = useState(false);
  const center = {
    latitude: Number(latitude) || DEFAULT_CENTER.latitude,
    longitude: Number(longitude) || DEFAULT_CENTER.longitude,
  };
  // عند وضع/سحب الدبوس: حدّث الإحداثيات فوراً، ثم احسب العنوان بنسختيه (عربي+إنجليزي) مرة واحدة
  // كي يصبح التبديل بين اللغتين فورياً بلا أي استعلام شبكي لاحق.
  const handleMove = async (coords) => {
    onChange(coords);
    try {
      const lang = i18n.language;
      const { label } = await reverseGeocode(coords.latitude, coords.longitude, lang);
      if (!label) return;
      const primary = await localizeAddress(label, lang);
      const other = (await translateText(primary, lang === "ar" ? "en" : "ar", "address")) || primary;
      onChange({
        ...coords,
        label: primary,
        address_ar: lang === "ar" ? primary : other,
        address_en: lang === "en" ? primary : other,
      });
    } catch {
      /* يتجاهل فشل جلب العنوان ويُبقي الإحداثيات */
    }
  };

  const { error } = useMapLibre({
    container,
    center,
    radiusM: Number(radiusM) || 50,
    onMove: handleMove,
    lang: i18n.language,
    theme,
    satellite,
  });

  return (
    <div className="grid gap-3" data-testid="location-picker">
      <MapSearch onPick={(place) => onChange({ latitude: place.latitude, longitude: place.longitude, label: place.label })} />
      <div className="relative">
        <div
          ref={container}
          dir="ltr"
          data-testid="map-canvas"
          className={`${height} w-full overflow-hidden rounded-3xl border border-border/60 shadow-neu-in`}
        />
        <button
          type="button"
          data-testid="map-view-toggle"
          onClick={() => setSatellite((s) => !s)}
          className="absolute top-3 right-3 z-10 flex items-center gap-1.5 rounded-xl bg-background/90 px-3 py-1.5 text-[11px] font-bold shadow-neu-sm backdrop-blur transition hover:-translate-y-0.5"
        >
          {satellite ? <MapIcon className="h-3.5 w-3.5" /> : <Satellite className="h-3.5 w-3.5" />}
          {satellite ? t("map.standard") : t("map.satellite")}
        </button>
      </div>
      <p className="font-mono text-[11px] text-muted-foreground" dir="ltr" data-testid="map-coords">
        {center.latitude.toFixed(6)}, {center.longitude.toFixed(6)} · {Number(radiusM) || 50} m
      </p>
      <p className="text-[11px] text-muted-foreground">{t("map.hint")}</p>
      {error ? (
        <p className="text-[11px] font-bold text-destructive" data-testid="map-error">
          {t("map.loadError")}
        </p>
      ) : null}
    </div>
  );
};
